/**
 * Pack Extras - Handles selecting extra packs to add with the main order
 *
 * Each flavor line shows:
 * - Fixed quantity in the pack (doesn't change)
 * - "-" button to remove a pack from selection
 * - "+X" button to add a pack to selection (X = remaining slots)
 *
 * When submitting the form, all selected packs are added to cart along with the main product.
 */

class PackExtras {
  constructor() {
    this.container = document.querySelector('[data-pack-extras]');
    if (!this.container) return;

    this.cards = this.container.querySelectorAll('.pack-card-extras');
    this.selectedPacks = new Map(); // Map<variantId, { count, maxPacks, price, comparePrice }>

    // Sticky bar elements
    this.stickyBar = document.querySelector('[data-pack-extras-sticky]');
    this.stickySubmitBtn = document.querySelector('[data-pack-extras-submit]');

    // Price data from sticky bar
    this.coffretPrice = 0;
    this.coffretComparePrice = 0;
    this.extraPackPrice = 0;
    this.extraPackComparePrice = 0;
    this.qtyPerPack = 6;

    this.init();
  }

  init() {
    this.initPriceData();
    this.initCards();
    this.initStickyBar();
    this.setupAddToCartOverride();
  }

  initPriceData() {
    // Get coffret prices from submit button data attributes
    if (this.stickySubmitBtn) {
      const variantId = this.stickySubmitBtn.dataset.variantId;
      // These will be calculated from the first card's data
    }

    // Get extra pack price from first card
    const firstCard = this.cards[0];
    if (firstCard) {
      this.extraPackPrice = parseInt(firstCard.dataset.extraPrice, 10) || 0;
      this.extraPackComparePrice = parseInt(firstCard.dataset.extraComparePrice, 10) || this.extraPackPrice;
      this.qtyPerPack = parseInt(firstCard.dataset.qtyPerPack, 10) || 6;
    }

    // Get coffret price from main product form
    const mainSubmitBtn = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (mainSubmitBtn) {
      this.coffretPrice = parseInt(mainSubmitBtn.dataset.originalPrice, 10) || 0;
      this.coffretComparePrice = parseInt(mainSubmitBtn.dataset.comparePrice, 10) || this.coffretPrice;
    }

    // Fallback: get from variant price display
    if (!this.coffretPrice) {
      const priceEl = document.querySelector('.product-form__price [data-variant-price]');
      if (priceEl) {
        const priceText = priceEl.textContent.replace(/[^\d,\.]/g, '').replace(',', '.');
        this.coffretPrice = Math.round(parseFloat(priceText) * 100);
      }
    }
  }

  initCards() {
    this.cards.forEach(card => {
      const extraVariantId = card.dataset.extraVariantId;
      const maxPacks = parseInt(card.dataset.maxPacks, 10) || 6;
      const price = parseInt(card.dataset.extraPrice, 10) || 0;
      const comparePrice = parseInt(card.dataset.extraComparePrice, 10) || price;
      const baseQty = parseInt(card.dataset.baseQty, 10) || 1;
      const qtyPerPack = parseInt(card.dataset.qtyPerPack, 10) || 6;

      // Initialize selected packs count to 0
      this.selectedPacks.set(extraVariantId, {
        count: 0,
        maxPacks: maxPacks,
        price: price,
        comparePrice: comparePrice,
        baseQty: baseQty,
        qtyPerPack: qtyPerPack
      });

      // Setup button listeners
      const minusBtn = card.querySelector('[data-action="remove-pack"]');
      const plusBtn = card.querySelector('[data-action="add-pack"]');

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          this.decrementPack(extraVariantId, card);
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          this.incrementPack(extraVariantId, card);
        });
      }
    });
  }

  initStickyBar() {
    if (!this.stickyBar) return;

    // Setup scroll listener to show/hide sticky bar
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateStickyVisibility();
          lastScrollY = window.scrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Setup sticky bar CTA click
    if (this.stickySubmitBtn) {
      this.stickySubmitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart(this.stickySubmitBtn);
      });
    }

    // Initial check
    this.updateStickyVisibility();
    this.updateStickyPrices();
  }

  updateStickyVisibility() {
    if (!this.stickyBar) return;

    // Find the main add to cart button position
    const mainButton = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (!mainButton) {
      this.stickyBar.classList.add('is-open');
      return;
    }

    const buttonRect = mainButton.getBoundingClientRect();

    // Show sticky bar only when scrolled PAST the button (button is above viewport)
    if (buttonRect.bottom < 0) {
      this.stickyBar.classList.add('is-open');
    } else {
      this.stickyBar.classList.remove('is-open');
    }
  }

  incrementPack(variantId, card) {
    const data = this.selectedPacks.get(variantId);
    if (!data || data.count >= data.maxPacks) return;

    data.count++;
    this.selectedPacks.set(variantId, data);
    this.updateCardUI(card, data.count, data.maxPacks, data.baseQty, data.qtyPerPack);
    this.updateStickyPrices();
  }

  decrementPack(variantId, card) {
    const data = this.selectedPacks.get(variantId);
    if (!data || data.count <= 0) return;

    data.count--;
    this.selectedPacks.set(variantId, data);
    this.updateCardUI(card, data.count, data.maxPacks, data.baseQty, data.qtyPerPack);
    this.updateStickyPrices();
  }

  updateCardUI(card, count, maxPacks, baseQty, qtyPerPack) {
    const minusBtn = card.querySelector('[data-action="remove-pack"]');
    const plusBtn = card.querySelector('[data-action="add-pack"]');
    const qtyDisplay = card.querySelector('[data-qty-display]');

    // Update quantity display: base quantity + (selected packs * qty per pack)
    if (qtyDisplay) {
      const totalQty = baseQty + (count * qtyPerPack);
      qtyDisplay.textContent = totalQty;
    }

    // Enable/disable buttons based on selection count
    if (minusBtn) {
      minusBtn.disabled = count <= 0;
    }

    if (plusBtn) {
      plusBtn.disabled = count >= maxPacks;
    }
  }

  updateStickyPrices() {
    // Calculate totals
    let extrasTotal = 0;
    let extrasCompareTotal = 0;
    let totalPacksSelected = 0;
    let totalBarsCount = 0;

    this.selectedPacks.forEach(data => {
      extrasTotal += data.count * data.price;
      extrasCompareTotal += data.count * data.comparePrice;
      totalPacksSelected += data.count;
      // Count total bars: base qty + (selected packs * qty per pack)
      totalBarsCount += data.baseQty + (data.count * data.qtyPerPack);
    });

    // Prix global = prix après réduction
    const totalPrice = this.coffretPrice + extrasTotal;
    // Prix barré = prix avant réduction
    const totalComparePrice = this.coffretComparePrice + extrasCompareTotal;

    // Économie par pack = montant fixe de réduction
    const savingsPerPack = this.extraPackComparePrice - this.extraPackPrice;

    // Prix par barre = prix global / nombre total de barres
    const pricePerBar = totalBarsCount > 0 ? Math.round(totalPrice / totalBarsCount) : 0;

    // Format prices
    const formatPrice = window.formatPrice;

    // Update main add-to-cart button price
    const mainButton = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (mainButton) {
      const priceEl = mainButton.querySelector('.product-form__price');
      const comparePriceEl = mainButton.querySelector('.product-form__compare-price');

      if (priceEl) {
        priceEl.textContent = formatPrice(totalPrice);
      }
      if (comparePriceEl) {
        comparePriceEl.textContent = formatPrice(totalComparePrice);
        comparePriceEl.style.display = totalComparePrice > totalPrice ? '' : 'none';
      }
    }

    // Update sticky bar if present
    if (!this.stickyBar) return;

    // Update desktop total price
    const totalPriceEl = this.stickyBar.querySelector('[data-total-price]');
    if (totalPriceEl) {
      totalPriceEl.textContent = formatPrice(totalPrice);
    }

    const compareTotalPriceEl = this.stickyBar.querySelector('[data-compare-total-price]');
    if (compareTotalPriceEl) {
      compareTotalPriceEl.textContent = formatPrice(totalComparePrice);
      compareTotalPriceEl.style.display = totalComparePrice > totalPrice ? '' : 'none';
    }

    // Update mobile total price (in CTA button)
    const totalPriceMobileEl = this.stickyBar.querySelector('[data-total-price-mobile]');
    if (totalPriceMobileEl) {
      totalPriceMobileEl.textContent = formatPrice(totalPrice);
    }

    const compareTotalPriceMobileEl = this.stickyBar.querySelector('[data-compare-total-price-mobile]');
    if (compareTotalPriceMobileEl) {
      compareTotalPriceMobileEl.textContent = formatPrice(totalComparePrice);
      compareTotalPriceMobileEl.style.display = totalComparePrice > totalPrice ? '' : 'none';
    }

    // Update savings amount (économie FIXE par coffret - ne change pas)
    const savingsEl = this.stickyBar.querySelector('[data-savings-amount]');
    const savingsElMobile = this.stickyBar.querySelector('[data-savings-amount-mobile]');
    if (savingsEl) savingsEl.textContent = formatPrice(savingsPerPack);
    if (savingsElMobile) savingsElMobile.textContent = formatPrice(savingsPerPack);

    // Update price per bar
    const pricePerBarEl = this.stickyBar.querySelector('[data-price-per-bar]');
    const pricePerBarMobileEl = this.stickyBar.querySelector('[data-price-per-bar-mobile]');
    if (pricePerBarEl) pricePerBarEl.textContent = formatPrice(pricePerBar);
    if (pricePerBarMobileEl) pricePerBarMobileEl.textContent = formatPrice(pricePerBar);
  }

  getSelectedPacksItems() {
    const items = [];
    this.selectedPacks.forEach((data, variantId) => {
      if (data.count > 0) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: data.count
        });
      }
    });
    return items;
  }

  getTotalExtrasPrice() {
    let total = 0;
    this.selectedPacks.forEach(data => {
      total += data.count * data.price;
    });
    return total;
  }

  async handleAddToCart(button) {
    const variantId = button.getAttribute('data-variant-id') || button.getAttribute('variant-id');
    const qty = button.getAttribute('data-quantity') || 1;

    // Disable button and show loading
    const originalText = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    // Update button text based on which button was clicked
    if (button.classList.contains('pack-extras-sticky__cta')) {
      button.innerHTML = '<span class="pack-extras-sticky__cta-text p-large fw-7">AJOUT...</span>';
    } else {
      button.innerHTML = '<span class="fw-6">AJOUT...</span>';
    }

    try {
      const sellingPlanInput = document.querySelector('[data-selling-plan-input]');
      const sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
      const productFormEl = this.container?.closest('product-form');
      const activePlanName = productFormEl?.getActiveSellingPlanName?.() || '';

      // Build extra items list with collection ID (matched in Liquid)
      const extraItems = this.getSelectedPacksItems();
      const subscriptionData = productFormEl?.subscriptionData;
      const fallbackSellingPlanId = subscriptionData?.sellingPlans?.[0]?.id
        ? String(subscriptionData.sellingPlans[0].id)
        : sellingPlanId;

      const extraItemsWithMeta = extraItems.map(item => {
        const card = this.container?.querySelector(`[data-extra-variant-id="${item.id}"]`);
        const collectionId = card?.dataset.extraCollectionId || '';
        let itemSellingPlanId = '';
        if (sellingPlanId) {
          itemSellingPlanId = ProductTemplate.findMatchingSellingPlanId(
            card, activePlanName, 'data-extra-selling-plans'
          ) || sellingPlanId;
        } else {
          // One-time: try extra's first plan, then fallback to bundle's first plan
          try {
            const plans = JSON.parse(card?.getAttribute('data-extra-selling-plans') || '[]');
            if (plans.length > 0) itemSellingPlanId = String(plans[0].id);
          } catch(e) {}
          if (!itemSellingPlanId) itemSellingPlanId = fallbackSellingPlanId;
        }
        return { ...item, collectionId, sellingPlanId: itemSellingPlanId, productId: card?.dataset.extraProductId || '' };
      });

      let rbId = '';
      let bundleJson = '';

      // Check if product has selling plans (generate _rb_id even for one-time)
      const hasSellingPlans = subscriptionData?.sellingPlans?.length > 0;

      if (hasSellingPlans && typeof recharge !== 'undefined' && recharge.bundle) {
        const bundleProductId = productFormEl?.dataset.productId || '';
        const rechargeCollections = productFormEl?.subscriptionData?.rechargeCollections || [];
        const mainCollectionId = rechargeCollections.length > 0 ? String(rechargeCollections[0]) : '';

        // Use active selling plan, or first available for one-time
        const mainSellingPlanId = sellingPlanId ||
          (productFormEl?.subscriptionData?.sellingPlans?.[0]?.id ? String(productFormEl.subscriptionData.sellingPlans[0].id) : '');

        const selections = [{
          externalProductId: String(bundleProductId),
          externalVariantId: String(variantId),
          collectionId: String(mainCollectionId),
          quantity: parseInt(qty),
          sellingPlan: String(mainSellingPlanId)
        }];

        extraItemsWithMeta.forEach(item => {
          selections.push({
            externalProductId: String(item.productId),
            externalVariantId: String(item.id),
            collectionId: String(item.collectionId),
            quantity: item.quantity,
            sellingPlan: String(item.sellingPlanId)
          });
        });

        const bundle = {
          externalProductId: String(bundleProductId),
          externalVariantId: String(variantId),
          selections: selections
        };

        console.log('[Recharge PackExtras] bundle:', JSON.stringify(bundle, null, 2));

        const isValid = await recharge.bundle.validateBundle(bundle);
        console.log('[Recharge PackExtras] validateBundle:', isValid);

        const { valid, error } = await recharge.bundle.validateBundleSelection(bundle);
        console.log('[Recharge PackExtras] validateBundleSelection:', valid, error);

        if (isValid && valid) {
          rbId = await recharge.bundle.getBundleId(bundle);
          bundleJson = JSON.stringify(bundle);
          console.log('[Recharge PackExtras] _rb_id:', rbId);
        } else {
          console.error('[Recharge PackExtras] validation failed — validateBundle:', isValid, '— validateBundleSelection:', error, bundle);
        }
      } else if (hasSellingPlans) {
        console.warn('[Recharge PackExtras] recharge.bundle non disponible');
      }

      // 1. Add the main product (coffret)
      const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const mainProperties = { _random: randomId };
      if (rbId) {
        mainProperties._rb_id = rbId;
        mainProperties._rb_selections = bundleJson;
      }

      const mainBody = { id: variantId, quantity: qty, properties: mainProperties };
      if (sellingPlanId) mainBody.selling_plan = parseInt(sellingPlanId);

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(mainBody),
        credentials: 'same-origin',
      });

      // 2. Add all selected extra packs
      for (const item of extraItemsWithMeta) {
        const extraProperties = {};
        if (rbId) {
          extraProperties._rb_id = rbId;
          extraProperties._rb_selections = bundleJson;
        }

        const extraBody = { id: item.id, quantity: item.quantity, properties: extraProperties };
        if (sellingPlanId) extraBody.selling_plan = parseInt(item.sellingPlanId);

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(extraBody),
          credentials: 'same-origin',
        });
      }

      // Update button text
      if (button.classList.contains('pack-extras-sticky__cta')) {
        button.innerHTML = '<span class="pack-extras-sticky__cta-text p-large fw-7">AJOUTÉ</span>';
      } else {
        button.innerHTML = '<span class="fw-6">AJOUTÉ</span>';
      }

      // Dispatch event for sidecart
      document.dispatchEvent(new CustomEvent('product:added'));

      // Open sidecart
      const sideCart = document.querySelector('side-cart');
      if (sideCart) {
        sideCart.classList.add('open');
      }

      // Reset selections
      this.resetSelections();

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.innerHTML = originalText;
    } finally {
      setTimeout(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = originalText;
      }, 1500);
    }
  }

  setupAddToCartOverride() {
    const submitButton = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (!submitButton) return;

    // Clone and replace to remove existing listeners
    const newButton = submitButton.cloneNode(true);
    submitButton.parentNode.replaceChild(newButton, submitButton);

    newButton.addEventListener('click', async (e) => {
      e.preventDefault();
      this.handleAddToCart(newButton);
    });
  }

  resetSelections() {
    this.cards.forEach(card => {
      const variantId = card.dataset.extraVariantId;
      const data = this.selectedPacks.get(variantId);

      if (data) {
        data.count = 0;
        this.selectedPacks.set(variantId, data);

        // Reset button states
        const minusBtn = card.querySelector('[data-action="remove-pack"]');
        const plusBtn = card.querySelector('[data-action="add-pack"]');
        const qtyDisplay = card.querySelector('[data-qty-display]');

        if (minusBtn) minusBtn.disabled = true;
        if (plusBtn) plusBtn.disabled = false;

        // Reset quantity display to base quantity
        if (qtyDisplay) {
          qtyDisplay.textContent = data.baseQty;
        }
      }
    });

    // Reset sticky prices
    this.updateStickyPrices();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PackExtras();
});
